/**
 * #590: real Google Chrome on any platform — desktop Chrome or mobile
 * Chrome (Android `Chrome/`, iOS `CriOS/`). NOT Edge (`Edg/`, `EdgA/`, `EdgiOS`),
 * Opera (`OPR/`), Samsung Internet, or Brave.
 *
 * Different question from isChromeDesktop (GMA-1318, web push): the
 * registration gate cares about the browser family, not the form factor.
 * Desktop Brave ships a Chrome-identical UA, so `navigator.brave` is the
 * authoritative exclusion (see isChromeDesktop.js, PR #216).
 */
/**
 * An embedded WebView, not a browser the caregiver chose. Android System
 * WebView carries `Chrome/`, so it passed the gate and was handed the
 * registration form — and it is the single most likely place an emailed
 * invite is opened, because email and chat apps open links in-app.
 *
 * Two markers: the `; wv)` platform token, and a `Version/` token, which
 * real Chrome never sends but every WebView does.
 */
const WEBVIEW = /;\s*wv\)|\bVersion\/\d+(\.\d+)*\s+Chrome\//i;

export function isChromeBrowser() {
  if (typeof navigator === "undefined") return false;
  if (navigator.brave) return false;
  const ua = navigator.userAgent || "";
  if (/Edg(A|iOS)?\/|OP(R|iOS)\/|Brave\/|SamsungBrowser/i.test(ua)) return false;
  if (WEBVIEW.test(ua)) return false;
  return /Chrome\/|CriOS\//.test(ua);
}

/**
 * Short browser-family label for the register.browser_gate breadcrumb
 * (#590's confirming filter keys on data.browser). Order matters: every
 * Chromium derivative also carries the Chrome/ token, and everything
 * WebKit-based carries Safari/.
 */
export function browserFamily() {
  if (typeof navigator === "undefined") return "none";
  if (navigator.brave) return "brave";
  const ua = navigator.userAgent || "";
  if (/Edg(A|iOS)?\//i.test(ua)) return "edge";
  if (/OP(R|iOS)\//i.test(ua)) return "opera";
  if (/SamsungBrowser/i.test(ua)) return "samsung-internet";
  // Before the Chrome arm: a legacy Brave/ UA carries Chrome/ too, and
  // isChromeBrowser() gates it on the same token. Reaching Chrome first
  // reported a gated browser as "chrome" — the breadcrumb contradicting
  // the verdict it exists to explain.
  if (/Brave\//i.test(ua)) return "brave";
  // Also before the Chrome arm: a WebView carries Chrome/ too, and the
  // breadcrumb must name what the caregiver is actually stuck in.
  if (WEBVIEW.test(ua)) return "webview";
  if (/Chrome\/|CriOS\//.test(ua)) return "chrome";
  if (/Firefox\/|FxiOS/i.test(ua)) return "firefox";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

/**
 * #590: does this device register the googlechrome(s):// URL scheme, so the
 * interstitial can offer a one-tap "Open in Chrome"?
 *
 * iPhone is a UA question; iPad is not. Since iPadOS 13 the default is
 * desktop-class Safari, which sends a `Macintosh` UA with no iPad token —
 * indistinguishable from a real Mac except by touch points. Those iPads are
 * gated correctly and were being denied the one recovery the interstitial
 * offers them, so treat a touch-capable Macintosh as an iPad.
 */
export function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

/**
 * #590 follow-up: the incident caregiver was on macOS desktop Safari. macOS
 * Chrome registers only a bare `google-chrome:` launch scheme (verified from
 * its Info.plist; a URL payload is NOT carried — measured), so the mac
 * recovery is copy-the-link + launch Chrome, never a deep link.
 */
export function isMacDesktop() {
  if (typeof navigator === "undefined") return false;
  return /Macintosh/i.test(navigator.userAgent || "") && !isAppleTouchDevice();
}

// Telemetry helpers (operator directive 2026-08-27): every register event
// says how the caregiver opened the invite — platform, form factor, and
// whether an embedded in-app browser was involved — so the next incident is
// readable from Sentry instead of reconstructed from server logs.
export function devicePlatform() {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  if (isAppleTouchDevice()) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Macintosh/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  return "other";
}

export function deviceFormFactor() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad/i.test(ua)) return "tablet";
  // iPadOS 13+ hides the iPad token behind a Macintosh UA; touch is the tell.
  if (/Macintosh/i.test(ua) && isAppleTouchDevice()) return "tablet";
  if (/iPhone|iPod/i.test(ua)) return "mobile";
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? "mobile" : "tablet";
  return "desktop";
}

/**
 * Embedded in-app browsers — where email and chat apps actually open invite
 * links. Android is detected by the `; wv)`/`Version/…Chrome/` markers the
 * gate already excludes; an iOS WKWebView is an Apple mobile UA with no
 * trailing `Safari/` token and no Chrome token. Telemetry-only for iOS: the
 * gate itself is unchanged there in this change.
 */
export function isEmbeddedWebView() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (WEBVIEW.test(ua)) return true;
  // iPadOS 13+ WKWebViews carry a desktop-class Macintosh UA with no iPad
  // token — the same disguise deviceFormFactor() sees through via touch
  // points (review finding: the iPhone|iPad|iPod test alone reported
  // webview=false for exactly the embedded email-app case on an iPad).
  const appleMobileUa =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && isAppleTouchDevice());
  return appleMobileUa && !/Safari\//.test(ua) && !/CriOS\//.test(ua);
}
